import React, { useEffect, useState } from "react";
import { PuffLoader } from "react-spinners";
import MerchantAccountDrillDown from "./MerchantAccountDrillDown";
import MerchantStoreDrillDown from "./MerchantStoreDrillDown";
import { fetchRedemptionData, fetchActiveSubscriptionCount } from "./RedemptionChartUtilities";
import { api } from "../../utils/api";
import { CancelOutlined, CheckCircleOutlined } from "@mui/icons-material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MerchantRedemptionsCharts = ({ dataSource, appliedFilter }) => {
  const [loading, setLoading] = useState(true);
  const [countLoading, setCountLoading] = useState(true);
  const [redemptionData, setRedemptionsData] = useState([]);
  const [monthlySalesData, setMonthlySalesData] = useState({ labels: [], datasets: [] });
  const [dailySalesByDay, setDailySalesByDay] = useState({});
  const [dailySortedDates, setDailySortedDates] = useState([]);

  const currentPortal = sessionStorage.getItem("selectedPortal");
  const selectedMerchantStore = sessionStorage.getItem("selectedMerchantStore");

  const [totalCount, setTotalCount] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [activeSubscriptionCount, setActiveSubscriptionCount] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [paginatedData, setPaginatedData] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleRowClick = (redemptionId) => {
    const url = `/redemption/${redemptionId}`;
    window.open(url, "_blank");
  };

  const countTotalSalesByDay = (salesData) => {
    const totalSalesByDay = {};
    salesData.forEach((sale) => {
      const saleDate = new Date(sale.redemptionDateUTC || sale.createdOnUTC);
      if (!isNaN(saleDate.getTime())) {
        // Format as YYYY-MM-DD for daily aggregation
        const dateKey = `${saleDate.getFullYear()}-${String(saleDate.getMonth() + 1).padStart(2, '0')}-${String(saleDate.getDate()).padStart(2, '0')}`;
        if (!totalSalesByDay[dateKey]) {
          totalSalesByDay[dateKey] = { count: 0, totalPrice: 0 };
        }
        totalSalesByDay[dateKey].count += 1;
        const value = sale.totalValue != null ? parseFloat(sale.totalValue) : (parseFloat(sale.price) || 0);
        totalSalesByDay[dateKey].totalPrice += value;
      } else {
        console.error("Invalid Date:", sale.redemptionDateUTC || sale.createdOnUTC);
      }
    });
    return totalSalesByDay;
  };

  const getMonthName = (monthIndex) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ];
    return monthNames[monthIndex];
  };

  const sortDays = (dateKeys) => {
    return dateKeys.sort((a, b) => {
      return new Date(a) - new Date(b);
    });
  };

  const calculateDailySales = (data) => {
    const totalSalesByDay = countTotalSalesByDay(data);
    const sortedDates = sortDays(Object.keys(totalSalesByDay));

    setDailySalesByDay(totalSalesByDay);
    setDailySortedDates(sortedDates);

    setMonthlySalesData({
      labels: sortedDates.map((dateKey) => {
        const date = new Date(dateKey);
        return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
      }),
      datasets: [
        {
          label: "Payouts to Merchants by Day",
          // Round to 2 decimals so point/label values never show long floats.
          data: sortedDates.map((dateKey) =>
            Number((totalSalesByDay[dateKey]?.totalPrice || 0).toFixed(2))
          ),
          backgroundColor: "rgba(153, 102, 255, 0.6)",
          borderColor: "rgba(153, 102, 255, 1)",
          fill: false
        }
      ]
    });

    return { totalSalesByDay, sortedDates };
  };

  useEffect(() => {
    let cancelled = false;

    const applyRedemptionData = (data) => {
      if (cancelled) return;
      if (!data || !Array.isArray(data)) {
        console.warn("No redemption data received or data is not an array:", data);
        setRedemptionsData([]);
        setTotalCount(0);
        setTotalValue(0);
        setLoading(false);
        return;
      }

      const sortedData = data.sort(
        (a, b) =>
          new Date(b.redemptionDateUTC || b.createdOnUTC) - new Date(a.redemptionDateUTC || a.createdOnUTC)
      );
      setRedemptionsData(sortedData);

      const total = sortedData.length;
      const totalPrice = sortedData.reduce((sum, redemption) => {
        const value = redemption.totalValue != null ? parseFloat(redemption.totalValue) : (parseFloat(redemption.price) || 0);
        return sum + value;
      }, 0);

      setTotalCount(total);
      setTotalValue(totalPrice);
      calculateDailySales(sortedData);
      setLoading(false);
    };

    const fetchData = () => {
      if (Object.keys(appliedFilter).length === 0 && dataSource === "redemptions") {
        return;
      }

      const safeAppliedFilter = appliedFilter && typeof appliedFilter === "object" ? appliedFilter : {};
      const modifiedFilter = { ...safeAppliedFilter };
      if (currentPortal === "Merchant") {
        modifiedFilter.merchantStore = selectedMerchantStore;
      }

      setLoading(true);
      setCountLoading(true);

      fetchRedemptionData(dataSource, modifiedFilter)
        .then(applyRedemptionData)
        .catch((error) => {
          console.error("Error fetching redemption data:", error);
          if (!cancelled) setLoading(false);
        });

      fetchActiveSubscriptionCount(safeAppliedFilter)
        .then((activeSubsCount) => {
          if (cancelled) return;
          setActiveSubscriptionCount(activeSubsCount);
          setCountLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching active subscription count:", error);
          if (!cancelled) setCountLoading(false);
        });
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [appliedFilter, dataSource, currentPortal, selectedMerchantStore]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilter]);

  // Filter redemptions by search query
  const filteredData = redemptionData.filter((redemption) => {
    const customerName = `${redemption.vehicle?.customer?.firstName || ''} ${redemption.vehicle?.customer?.lastName || ''}`.toLowerCase();
    const vehicleRego = (redemption.vehicle?.rego || '').toLowerCase();
    const vehicleVin = (redemption.vehicle?.vin || '').toLowerCase();
    const productName = (redemption.product?.name || '').toLowerCase();
    const merchantStoreName = (redemption.merchantStore?.name || '').toLowerCase();
    const merchantUserName = `${redemption.merchantUser?.firstName || ''} ${redemption.merchantUser?.lastName || ''}`.toLowerCase();

    const query = searchQuery.toLowerCase();
    return (
      customerName.includes(query) ||
      vehicleRego.includes(query) ||
      vehicleVin.includes(query) ||
      productName.includes(query) ||
      merchantStoreName.includes(query) ||
      merchantUserName.includes(query)
    );
  });

  // Paginate the filtered data
  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setPaginatedData(filteredData.slice(startIndex, endIndex));
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const averageRedemptionCost = totalCount > 0 ? totalValue / totalCount : 0;
  const averageRedemptionRate =
    activeSubscriptionCount > 0 ? totalCount / activeSubscriptionCount : 0;

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center gap-3 py-4 text-sm text-gray-600" role="status">
          <PuffLoader color="purple" size={36} />
          Loading redemptions…
        </div>
      )}
      <div className="flex flex-wrap justify-center">
        {redemptionData.length > 0 ? (
          <div className="divide divide-y gap-4 flex flex-column py-2">
            {/* Payout figures note */}
            <div className="w-full mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-700">
              <strong>Note:</strong> Payout figures show merchant payouts (net of admin fee, GST inclusive).
            </div>
            {/* Summary Statistics */}
            <div className="w-full flex justify-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg flex-wrap">
              <div className="text-center p-4 bg-white rounded shadow min-w-[160px]">
                <div className="text-2xl font-bold text-blue-600">{totalCount.toLocaleString()}</div>
                <div className="text-sm text-gray-600 mt-1">Total Redemptions</div>
              </div>
              <div className="text-center p-4 bg-white rounded shadow min-w-[160px]">
                <div className="text-2xl font-bold text-green-600">${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-sm text-gray-600 mt-1">Total Value</div>
              </div>
              <div className="text-center p-4 bg-white rounded shadow min-w-[160px]">
                <div className="text-2xl font-bold text-orange-600">${averageRedemptionCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-sm text-gray-600 mt-1">Avg Redemption Cost</div>
              </div>
              <div className="text-center p-4 bg-white rounded shadow min-w-[160px]">
                <div className="text-2xl font-bold text-indigo-600">
                  {countLoading ? "…" : averageRedemptionRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-sm text-gray-600 mt-1">Avg Redemption Rate</div>
                <div className="text-xs text-gray-500 mt-1">
                  ({countLoading ? "…" : activeSubscriptionCount.toLocaleString()} active {activeSubscriptionCount === 1 ? 'subscription' : 'subscriptions'})
                </div>
              </div>
            </div>
            
            <div className="w-full flex justify-center items-center flex-wrap gap-2">
              <MerchantAccountDrillDown redemptionData={redemptionData} />

              {/* <MerchantStoreDrillDown redemptionData={redemptionData} /> */}
            </div>
            <div className="w-full flex flex-column max-w-[800px] justify-center items-center flex-wrap gap-2">
              <h2>Bills / Payouts to Merchants by Day</h2>
              <Line
                data={monthlySalesData}
                options={{
                  animation: false,
                  interaction: { mode: "nearest", intersect: true },
                  elements: { point: { radius: 2, hoverRadius: 4 } },
                  plugins: {
                    datalabels: {
                      display: false
                    },
                    tooltip: {
                      callbacks: {
                        label: function (tooltipItem) {
                          const dateKey = dailySortedDates[tooltipItem.dataIndex];
                          const salesData = dailySalesByDay[dateKey];
                          const totalPrice = salesData?.totalPrice || 0;
                          return [
                            `Total Payout: $${typeof totalPrice === "number" ? totalPrice.toFixed(2) : totalPrice}`,
                            `Count: ${salesData?.count || 0}`,
                          ];
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => {
                          const num = typeof value === "string" ? Number(value) : value;
                          return Number.isFinite(num) ? num.toFixed(2) : value;
                        }
                      }
                    }
                  },
                }}
              />
            </div>

            <div className="w-full redemptionRecords">
              <div className="mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Search redemptions by customer, vehicle, product, merchant store, or merchant user..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="overflow-x-auto shadow-md rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Verified
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: "180px" }}>
                        Customer / Vehicle / Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: "150px" }}>
                        Merchant Store / User / Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: "130px" }}>
                        Date / Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((redemption, index) => {
                      const customerName = `${redemption.vehicle?.customer?.firstName || ''} ${redemption.vehicle?.customer?.lastName || ''}`;
                      const vehicleVin = redemption.vehicle?.vin || "";
                      const vehicleRego = redemption.vehicle?.rego || "";
                      // Format: "VIN (rego)" or just what's available
                      const vehicleDisplay = (() => {
                        if (vehicleVin && vehicleRego) {
                          return `${vehicleVin} (${vehicleRego})`;
                        } else if (vehicleVin) {
                          return vehicleVin;
                        } else if (vehicleRego) {
                          return vehicleRego;
                        }
                        return "N/A";
                      })();
                      const productName = redemption.product?.name;
                      const merchantStoreName = redemption.merchantStore ? `${redemption.merchantStore?.name}` : "";
                      const merchantUserName = redemption.merchantUser
                        ? `${redemption.merchantUser?.firstName} ${redemption.merchantUser?.lastName}`
                        : "";
                      const invoiceStatus = redemption.invoiceStatus;
                      const rowNumber = (currentPage - 1) * pageSize + index + 1;

                      return (
                        <tr
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleRowClick(redemption._id)}
                          key={redemption._id}
                        >
                          <td className="px-3 py-6 whitespace-nowrap text-sm" style={{ color: "#000", width: "5%" }}>{rowNumber}</td>
                          <td className="px-3 py-6 whitespace-nowrap text-sm" style={{ color: "#000", width: "8%" }}>
                            {redemption.verified ? (
                              <CheckCircleOutlined className="success" />
                            ) : (
                              <CancelOutlined className="error" />
                            )}
                          </td>
                          <td className="px-4 py-6 text-sm" style={{ color: "#000", minWidth: "180px" }}>
                            <div className="flex flex-col gap-2">
                              <div className="font-medium leading-tight break-words" style={{ color: "#000", fontSize: "0.875rem" }}>{customerName}</div>
                              <div className="text-xs leading-tight break-words" style={{ color: "#000" }}>{vehicleDisplay}</div>
                              <div className="text-xs leading-tight break-words" style={{ color: "#000" }}>{productName}</div>
                            </div>
                          </td>
                          <td className="px-4 py-6 text-sm" style={{ color: "#000", minWidth: "150px" }}>
                            <div className="flex flex-col gap-2">
                              <div className="leading-tight break-words" style={{ color: "#000", fontSize: "0.875rem" }}>{merchantStoreName || "N/A"}</div>
                              <div className="text-xs leading-tight break-words" style={{ color: "#000" }}>{merchantUserName || "N/A"}</div>
                              <div className="text-xs leading-tight break-words" style={{ color: "#000" }}>{invoiceStatus || "N/A"}</div>
                            </div>
                          </td>
                          <td className="px-4 py-6 text-sm" style={{ color: "#000", minWidth: "130px" }}>
                            <div className="flex flex-col gap-2">
                              <div className="leading-tight" style={{ color: "#000", fontSize: "0.875rem" }}>
                                {redemption.redemptionDateUTC ? new Date(redemption.redemptionDateUTC).toLocaleDateString() : "N/A"}
                              </div>
                              <div className="text-xs leading-tight" style={{ color: "#000" }}>
                                {redemption.redemptionDateUTC ? new Date(redemption.redemptionDateUTC).toLocaleTimeString() : "N/A"}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4 p-4 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded ${currentPage === totalPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          !loading && <p className="text-center text-gray-500 py-8">No redemption data available</p>
        )}
      </div>
    </>
  );
};

export default MerchantRedemptionsCharts;
